import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import CreditsBadge from './CreditsBadge'
import { HelpButton } from './HelpModal'
import { ChatButton } from './ChatWidget'
import {
  LayoutDashboard,
  Shield,
  FileText,
  FolderOpen,
  AlertTriangle,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Layers,
  Activity,
  BarChart3,
  Cloud,
  Building2,
  ClipboardList,
  Link2,
  Radar,
  HelpCircle,
  Sliders,
  Gauge,
  Target,
  Key,
  Server,
  Check,
  RefreshCw,
  Skull,
  GraduationCap,
  BookOpen,
  Fish,
  Network,
} from 'lucide-react'

interface AvailableTenant {
  id: string
  slug: string
  name: string | null
  role: string
}

interface LayoutProps {
  children: React.ReactNode
}

// Navigation organized into collapsible groups per Issue #251
type NavItem = { name: string; href: string; icon: any }
type NavGroup = { name: string; icon: any; items: NavItem[] }

const navigationGroups: NavGroup[] = [
  {
    name: 'Compliance',
    icon: Shield,
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Tasks', href: '/tasks', icon: ClipboardList },
      { name: 'Controls', href: '/controls', icon: Shield },
      { name: 'Internal Controls', href: '/internal-controls', icon: Layers },
      { name: 'Evidence', href: '/evidence', icon: FileText },
      // { name: 'Mappings', href: '/mappings', icon: Link2 }, // Hidden - IC→Framework mappings hardcoded (#377)
    ],
  },
  {
    name: 'Documents',
    icon: FolderOpen,
    items: [
      { name: 'Documents', href: '/documents', icon: FolderOpen },
    ],
  },
  {
    name: 'Risk',
    icon: AlertTriangle,
    items: [
      { name: 'Risks', href: '/risks', icon: AlertTriangle },
      { name: 'Risk Appetite', href: '/risk-appetite', icon: Target },
      { name: 'KRIs', href: '/kris', icon: Gauge },
      { name: 'Dark Web', href: '/dark-web', icon: Skull },
      { name: 'TPRM', href: '/tprm', icon: Building2 },
      { name: 'Scanners', href: '/scanners', icon: Radar },
      { name: 'Assets', href: '/assets', icon: Server },
    ],
  },
  {
    name: 'Reports',
    icon: BarChart3,
    items: [
      { name: 'Reports', href: '/reports', icon: BarChart3 },
      { name: 'Gap Analysis', href: '/reports/gap-analysis', icon: Target },
      { name: 'Network Diagrams', href: '/diagrams', icon: Network },
      { name: 'Activity Log', href: '/activity-log', icon: Activity },
    ],
  },
  {
    name: 'Training',
    icon: GraduationCap,
    items: [
      { name: 'Training Dashboard', href: '/training', icon: GraduationCap },
      { name: 'Phishing Simulations', href: '/phishing', icon: Fish },
    ],
  },
  {
    name: 'Admin',
    icon: Settings,
    items: [
      { name: 'Frameworks', href: '/frameworks', icon: Layers },
      { name: 'Users', href: '/users', icon: Users },
      { name: 'Permissions', href: '/permissions', icon: Key },
      { name: 'Integrations', href: '/integrations', icon: Cloud },
      { name: 'Custom Fields', href: '/custom-fields', icon: Sliders },
      { name: 'Settings', href: '/settings', icon: Settings },
    ],
  },
]

export default function Layout({ children }: LayoutProps) {
  const { user, logout, getTenant, getAvailableTenants, switchTenant, isSwitchingTenant } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [tenantMenuOpen, setTenantMenuOpen] = useState(false)
  const [availableTenants, setAvailableTenants] = useState<AvailableTenant[]>([])
  const [switchError, setSwitchError] = useState<string | null>(null)

  // Load available tenants on mount
  useEffect(() => {
    const tenants = getAvailableTenants()
    setAvailableTenants(tenants)
  }, [getAvailableTenants])

  const handleSwitchTenant = async (targetTenant: AvailableTenant) => {
    const currentTenant = getTenant()
    if (currentTenant?.slug === targetTenant.slug) {
      // Already on this tenant
      setTenantMenuOpen(false)
      return
    }

    setSwitchError(null)
    try {
      await switchTenant(targetTenant.slug)
      // switchTenant will reload the page on success
    } catch (error) {
      setSwitchError(error instanceof Error ? error.message : 'Failed to switch organization')
    }
  }

  // Track which nav groups are expanded - default all open
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    // Find which group contains the current path and expand it
    const activeGroup = navigationGroups.find(g =>
      g.items.some(item => item.href === location.pathname)
    )
    return new Set(activeGroup ? [activeGroup.name] : ['Compliance'])
  })

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupName)) {
        next.delete(groupName)
      } else {
        next.add(groupName)
      }
      return next
    })
  }

  // Get current tenant info for display in header
  const tenant = getTenant()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white border-r border-gray-100">
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-100">
            <div className="flex items-center">
              <img src="/logos/clarantis-icon.svg" alt="Clarantis" className="h-8 w-8" />
              <span className="ml-2 text-xl font-heading font-bold text-primary-700 tracking-snug">Clarantis</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto p-3">
            {navigationGroups.map((group) => {
              const isExpanded = expandedGroups.has(group.name)
              const hasActiveItem = group.items.some(item => location.pathname === item.href)
              return (
                <div key={group.name} className="mb-1">
                  <button
                    onClick={() => toggleGroup(group.name)}
                    className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      hasActiveItem ? 'text-primary-700 bg-primary-50/60' : 'text-gray-500 hover:text-gray-700 hover:bg-surface-100'
                    }`}
                  >
                    <div className="flex items-center">
                      <group.icon className="h-4 w-4 mr-2.5" />
                      {group.name}
                    </div>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  {isExpanded && (
                    <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 border-gray-100 pl-3">
                      {group.items.map((item) => {
                        const isActive = location.pathname === item.href
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center px-3 py-1.5 rounded-lg text-sm transition-all duration-150 ${
                              isActive
                                ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-500 -ml-[calc(0.75rem+2px)] pl-[calc(0.75rem+2px)]'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-surface-100'
                            }`}
                          >
                            <item.icon className={`h-4 w-4 mr-2.5 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                            {item.name}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
            {/* Mobile bottom links */}
            <div className="border-t border-gray-100 pt-3 mt-3 space-y-1">
              <Link
                to="/help"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                  location.pathname === '/help'
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-surface-100'
                }`}
              >
                <HelpCircle className="h-4 w-4 mr-2.5" />
                Help & Docs
              </Link>
            </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar - z-10 ensures modal overlays (z-50+) appear above */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-10 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-100">
          <div className="flex h-16 items-center px-6 border-b border-gray-100">
            <img src="/logos/clarantis-icon.svg" alt="Clarantis" className="h-8 w-8" />
            <span className="ml-2 text-xl font-heading font-bold text-primary-700 tracking-snug">Clarantis</span>
          </div>
          <nav className="flex-1 overflow-y-auto p-3">
            {navigationGroups.map((group) => {
              const isExpanded = expandedGroups.has(group.name)
              const hasActiveItem = group.items.some(item => location.pathname === item.href)
              return (
                <div key={group.name} className="mb-1">
                  <button
                    onClick={() => toggleGroup(group.name)}
                    className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      hasActiveItem ? 'text-primary-700 bg-primary-50/60' : 'text-gray-500 hover:text-gray-700 hover:bg-surface-100'
                    }`}
                  >
                    <div className="flex items-center">
                      <group.icon className="h-4 w-4 mr-2.5" />
                      {group.name}
                    </div>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  {isExpanded && (
                    <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 border-gray-100 pl-3">
                      {group.items.map((item) => {
                        const isActive = location.pathname === item.href
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`flex items-center px-3 py-1.5 rounded-lg text-sm transition-all duration-150 ${
                              isActive
                                ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-500 -ml-[calc(0.75rem+2px)] pl-[calc(0.75rem+2px)]'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-surface-100'
                            }`}
                          >
                            <item.icon className={`h-4 w-4 mr-2.5 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                            {item.name}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>
          <div className="border-t border-gray-100 p-3 space-y-1">
            <Link
              to="/help"
              className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                location.pathname === '/help'
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-surface-100'
              }`}
            >
              <HelpCircle className="h-4 w-4 mr-2.5" />
              Help & Docs
            </Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex-1 lg:hidden" />

            <div className="flex items-center gap-4">
              {/* Tenant Switcher */}
              {tenant && (
                <div className="relative hidden sm:block">
                  {availableTenants.length > 1 ? (
                    // Multiple tenants - show dropdown
                    <>
                      <button
                        onClick={() => setTenantMenuOpen(!tenantMenuOpen)}
                        disabled={isSwitchingTenant}
                        className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 rounded-lg border border-primary-100 hover:bg-primary-100 transition-colors disabled:opacity-50"
                      >
                        {isSwitchingTenant ? (
                          <RefreshCw className="h-4 w-4 text-primary-600 animate-spin" />
                        ) : (
                          <Building2 className="h-4 w-4 text-primary-600" />
                        )}
                        <span
                          className="text-sm font-medium text-primary-700 max-w-[200px] truncate"
                          title={tenant.name || tenant.slug}
                        >
                          {isSwitchingTenant ? 'Switching...' : (tenant.name || tenant.slug)}
                        </span>
                        <ChevronDown className={`h-3 w-3 text-primary-500 transition-transform ${tenantMenuOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {tenantMenuOpen && !isSwitchingTenant && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => {
                              setTenantMenuOpen(false)
                              setSwitchError(null)
                            }}
                          />
                          <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-elevated border border-gray-100 py-1 z-20">
                            <div className="px-4 py-2 border-b border-gray-100">
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Switch Organization
                              </p>
                            </div>
                            {switchError && (
                              <div className="px-4 py-2 bg-red-50 border-b border-red-100">
                                <p className="text-xs text-red-600">{switchError}</p>
                              </div>
                            )}
                            <div className="py-1 max-h-64 overflow-y-auto">
                              {availableTenants.map((t) => {
                                const isCurrentTenant = tenant.slug === t.slug
                                return (
                                  <button
                                    key={t.id}
                                    onClick={() => handleSwitchTenant(t)}
                                    disabled={isCurrentTenant}
                                    className={`flex items-center justify-between w-full px-4 py-2 text-sm transition-colors ${
                                      isCurrentTenant
                                        ? 'bg-primary-50 text-primary-700 cursor-default'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Building2 className={`h-4 w-4 flex-shrink-0 ${isCurrentTenant ? 'text-primary-600' : 'text-gray-400'}`} />
                                      <div className="text-left min-w-0">
                                        <p className="font-medium truncate" title={t.name || t.slug}>
                                          {t.name || t.slug}
                                        </p>
                                        <p className="text-xs text-gray-500 capitalize">{t.role}</p>
                                      </div>
                                    </div>
                                    {isCurrentTenant && (
                                      <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />
                                    )}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    // Single tenant - just display
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 rounded-lg border border-primary-100">
                      <Building2 className="h-4 w-4 text-primary-600" />
                      <span
                        className="text-sm font-medium text-primary-700 max-w-[200px] truncate"
                        title={tenant.name || tenant.slug}
                      >
                        {tenant.name || tenant.slug}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Credits Badge */}
              <CreditsBadge />

              {/* Help Button */}
              <HelpButton />

              {/* Chat Button - Virtual CISO */}
              <ChatButton />

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-100 transition-colors"
                >
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center ring-2 ring-white shadow-sm">
                    <span className="text-sm font-medium text-white">
                      {user?.username?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-gray-700">
                    {user?.username}
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </button>

                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-elevated border border-gray-100 py-1 z-20">
                      <div className="px-4 py-2.5 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.username}
                        </p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                        <p className="text-xs text-primary-600 capitalize mt-1">
                          {user?.role}
                        </p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-surface-100 transition-colors"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  )
}
